/**
 * Approve Request Modal Component
 *
 * Modal dialog for approving access requests with review notes and whitelist option
 */

import React, { useState } from 'react';
import type { AccessRequest } from '../../../types/access-control';

interface ApproveRequestModalProps {
  request: AccessRequest;
  onConfirm: (reviewNotes: string, addToWhitelist: boolean) => Promise<void>;
  onCancel: () => void;
}

export const ApproveRequestModal: React.FC<ApproveRequestModalProps> = ({
  request,
  onConfirm,
  onCancel,
}) => {
  const [reviewNotes, setReviewNotes] = useState('');
  const [addToWhitelist, setAddToWhitelist] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await onConfirm(reviewNotes, addToWhitelist);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve request');
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Approve Access Request
          </h2>

          <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
            <p className="text-sm font-medium text-gray-700">
              {request.name || 'Unknown User'}
            </p>
            <p className="text-xs text-gray-600">{request.email}</p>
            {request.reason && (
              <p className="text-xs text-gray-500 mt-2">
                <span className="font-medium">Reason: </span>
                {request.reason}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="reviewNotes"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Review Notes (Optional)
                </label>
                <textarea
                  id="reviewNotes"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Add any notes about this approval..."
                  disabled={isSubmitting}
                />
              </div>

              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="addToWhitelist"
                    type="checkbox"
                    checked={addToWhitelist}
                    onChange={(e) => setAddToWhitelist(e.target.checked)}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="ml-3">
                  <label
                    htmlFor="addToWhitelist"
                    className="text-sm font-medium text-gray-700"
                  >
                    Add to whitelist
                  </label>
                  <p className="text-xs text-gray-500">
                    Add this user's email to the whitelist for automatic future access
                  </p>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 px-4 py-2 text-sm font-semibold bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-sm font-semibold bg-green-600 text-white rounded hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Approving...' : 'Approve Request'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
